// hz/46: Zerbay sticky add-to-cart bar (PDPC-36, PDPC-37, SET-67, HZD-21, FTR-28).
// Shows the bar block (blocks/zerbay-sticky-bar.liquid) while a size is chosen and the watched element (Horizon's add
// button in buy mode, the notify panel in notify mode) is entirely off screen. It never adds to the cart itself: a buy
// tap clicks Horizon's add button, which runs the Zerbay add path from zerbay-pdp.js.
import {
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const FOOTER_MARGIN = 200;
const NBSP = '\u00a0';
const BANNER_SELECTOR = '#shopify-pc__banner, .shopify-pc__banner__dialog';
const BUSY_MS = 500;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const started = new WeakSet();

/**
 * @param {Element | null} el
 * @returns {boolean} true when the element has a layout box
 */
const isRendered = (el) => Boolean(el) && el.getClientRects().length > 0;

/** @returns {boolean} true when the element has a layout box, a height and is not visibility hidden */
const isVisibleBox = (el) => isRendered(el) && el.offsetHeight > 0 && getComputedStyle(el).visibility !== 'hidden';

/** @returns {Element | null} the footer when it is rendered */
const findFooter = () => {
  const footer = document.querySelector('footer');
  if (isRendered(footer)) return footer;
  const group = document.querySelector('.shopify-section-group-footer-group');
  return isRendered(group) ? group : null;
};

class ZerbayStickyBar {
  /** @param {HTMLElement} bar */
  constructor(bar) {
    this.bar = bar;
    this.root = bar.closest('.zb-pdp');
    this.button = bar.querySelector('.zb-bar__btn');
    this.labelEl = bar.querySelector('[data-zb-bar-label]');
    this.lineEl = bar.querySelector('[data-zb-bar-line]');
    this.photoBox = bar.querySelector('[data-zb-bar-photo]');
    this.photoImg = this.photoBox?.querySelector('img') ?? null;
    this.labels = { buy: '', notify: '' };
    try {
      this.labels = { ...this.labels, ...JSON.parse(bar.querySelector('script[data-zb-bar-data]')?.textContent || '{}') };
    } catch (_) {
      // labels stay empty; the markup label is kept
    }
    this.variants = {};
    try {
      const island = this.root?.querySelector('script[data-zb-pdp-state]');
      this.variants = JSON.parse(island?.textContent || '{}').variants ?? {};
    } catch (_) {
      // no price on the second line
    }

    this.state = {
      sizeChosen: this.root?.dataset.zbSizeChosen !== 'false',
      stock: this.root?.dataset.zbStock ?? '',
      variantId: null,
    };
    this.up = false;
    this.frame = 0;
    this.busyTimer = 0;
    this.watched = null;
    this.observedFooter = null;
    this.banners = [];
    this.scrollTarget = null;
    this.lastSrc = '';
    this.lastFillKey = '';

    this.schedule = this.schedule.bind(this);
    this.onState = this.onState.bind(this);
    this.onTap = this.onTap.bind(this);
    this.onContainerChange = this.onContainerChange.bind(this);

    if (!this.root || !this.button) return;
    // PLAN section 6: zerbay-pdp.js keeps the latest zb:pdp-state detail on the root; read it before listening
    if (this.root.zbPdpState) this.applyState(this.root.zbPdpState);
    this.start();
  }

  start() {
    this.io = new IntersectionObserver(this.schedule);
    this.attrObserver = new MutationObserver(this.schedule);
    this.attrObserver.observe(document.documentElement, { attributes: true });
    this.dialogObserver = new MutationObserver((records) => {
      // dialog[open] changes anywhere under body, and consent banners added later
      this.watchBanner();
      if (records.length) this.schedule();
    });
    this.dialogObserver.observe(document.body, { attributes: true, attributeFilter: ['open'], subtree: true });
    // banners can be inserted at any depth: react only when an added node is or contains one
    this.bodyChildObserver = new MutationObserver((records) => {
      // removals count too: a removed banner (or a wrapper holding one) must bring the bar back
      const touches = (n) =>
        n.nodeType === 1 &&
        (n.matches(BANNER_SELECTOR) || Boolean(n.querySelector(BANNER_SELECTOR)) || this.banners.some((b) => n === b || n.contains(b)));
      const hit = records.some((r) => [...r.addedNodes].some(touches) || [...r.removedNodes].some(touches));
      if (!hit) return;
      this.watchBanner();
      this.schedule();
    });
    this.bodyChildObserver.observe(document.body, { childList: true, subtree: true });
    this.bannerObserver = new MutationObserver(this.schedule);

    this.root.addEventListener('zb:pdp-state', this.onState);
    this.button.addEventListener('click', this.onTap);
    window.addEventListener('scroll', this.schedule, { passive: true });
    window.addEventListener('resize', this.schedule);
    scrollContainerMediaQuery.addEventListener('change', this.onContainerChange);
    this.bindScrollTarget();
    this.watchBanner();
    this.schedule();
  }

  destroy() {
    cancelAnimationFrame(this.frame);
    clearTimeout(this.busyTimer);
    this.io?.disconnect();
    this.attrObserver?.disconnect();
    this.dialogObserver?.disconnect();
    this.bodyChildObserver?.disconnect();
    this.bannerObserver?.disconnect();
    this.root?.removeEventListener('zb:pdp-state', this.onState);
    window.removeEventListener('scroll', this.schedule);
    window.removeEventListener('resize', this.schedule);
    scrollContainerMediaQuery.removeEventListener('change', this.onContainerChange);
    this.scrollTarget?.removeEventListener('scroll', this.schedule);
    this.setUp(false, true);
  }

  bindScrollTarget() {
    const target = getScrollEventTarget();
    if (target === this.scrollTarget) return;
    this.scrollTarget?.removeEventListener('scroll', this.schedule);
    this.scrollTarget = target;
    this.scrollTarget.addEventListener('scroll', this.schedule, { passive: true });
  }

  onContainerChange() {
    this.bindScrollTarget();
    this.schedule();
  }

  /** @param {CustomEvent} event */
  onState(event) {
    this.applyState(event.detail);
    this.schedule();
  }

  /** @param {{sizeChosen?: boolean, stock?: string, variantId?: unknown} | null | undefined} detail */
  applyState(detail) {
    const d = detail ?? {};
    if (typeof d.sizeChosen === 'boolean') this.state.sizeChosen = d.sizeChosen;
    if (typeof d.stock === 'string') this.state.stock = d.stock;
    if (d.variantId != null) this.state.variantId = String(d.variantId);
  }

  watchBanner() {
    const banners = [...document.querySelectorAll(BANNER_SELECTOR)];
    if (banners.length === this.banners.length && banners.every((b, k) => b === this.banners[k])) return;
    this.bannerObserver.disconnect();
    this.banners = banners;
    for (const banner of banners) {
      this.bannerObserver.observe(banner, {
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'],
        childList: true,
        subtree: true,
      });
    }
  }

  schedule() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.update();
    });
  }

  get addButton() {
    return this.root.querySelector('add-to-cart-component button');
  }

  get notifyBlock() {
    return this.root.querySelector('[data-zb-notify]');
  }

  /** The notify panel (form row, footer, note, success line) is what the bar watches and scrolls to, not the whole block,
   *  which also holds the tall size-alternatives box. Falls back to the block when the hook is absent. */
  get notifyTarget() {
    const block = this.notifyBlock;
    return block?.querySelector('[data-zb-notify-panel]') ?? block;
  }

  get notifyField() {
    return this.notifyBlock?.querySelector('input[type="email"]') ?? null;
  }

  /** @returns {'buy' | 'notify' | 'none'} */
  get mode() {
    const { sizeChosen, stock } = this.state;
    if (!sizeChosen) return 'none';
    if (stock === 'in_stock') {
      const add = this.addButton;
      return isRendered(add) && !add.disabled ? 'buy' : 'none';
    }
    if (stock === 'continue_selling') {
      return isRendered(this.notifyField) ? 'notify' : 'none';
    }
    return 'none';
  }

  /** @param {Element | null} el entirely above or below the viewport */
  isOffScreen(el) {
    if (!isRendered(el)) return false;
    const rect = el.getBoundingClientRect();
    return rect.bottom <= 0 || rect.top >= window.innerHeight;
  }

  /** @param {Element | null} footer */
  footerNear(footer) {
    if (!footer) return false;
    return footer.getBoundingClientRect().top < window.innerHeight + FOOTER_MARGIN;
  }

  overlayOpen() {
    const html = document.documentElement;
    if (document.querySelector('dialog[open]')) return true;
    if (html.classList.contains('zb-scroll-lock') || html.hasAttribute('scroll-lock')) return true;
    return [...document.querySelectorAll(BANNER_SELECTOR)].some(isVisibleBox);
  }

  update() {
    if (!this.bar.isConnected) {
      this.destroy();
      return;
    }
    const mode = this.mode;
    const watched = mode === 'buy' ? this.addButton : mode === 'notify' ? this.notifyTarget : null;
    const footer = findFooter();
    this.observeTargets(watched, footer);

    const show = mode !== 'none' && this.isOffScreen(watched) && !this.footerNear(footer) && !this.overlayOpen();
    // fill only when the bar is (about to be) shown or the state / mode changed since the last fill
    const { sizeChosen, stock, variantId } = this.state;
    const fillKey = [mode, sizeChosen, stock, variantId].join('|');
    if (mode !== 'none' && (show || this.up || fillKey !== this.lastFillKey)) {
      this.fill(mode);
      this.lastFillKey = fillKey;
    }
    if (this.bar.dataset.zbMode !== mode) this.bar.dataset.zbMode = mode;
    this.setUp(show);
  }

  observeTargets(watched, footer) {
    if (watched === this.watched && footer === this.observedFooter) return;
    this.io.disconnect();
    if (watched) this.io.observe(watched);
    if (footer) this.io.observe(footer);
    this.watched = watched;
    this.observedFooter = footer;
  }

  /** @param {boolean} show @param {boolean} [instant] */
  setUp(show, instant = false) {
    const { bar, button } = this;
    if (show === this.up && !instant) return;
    this.up = show;
    const html = document.documentElement;
    // another bar instance (theme editor reload) that is still up keeps the class
    const otherUp = [...document.querySelectorAll('[data-zb-bar].zb-bar--up')].some((b) => b !== bar);
    html.classList.toggle('zb-bar-up', show || otherUp);
    if (show) {
      bar.hidden = false;
      bar.inert = false;
      button.disabled = false;
      void bar.offsetHeight; // commit the closed position so the slide runs
      bar.classList.add('zb-bar--up');
      return;
    }
    // hide in the same frame: nothing stays tappable or visible while the condition is false
    bar.classList.remove('zb-bar--up');
    bar.inert = true;
    bar.hidden = true;
    button.disabled = true;
    this.clearBusy();
  }

  fill(mode) {
    const label = this.labels[mode];
    if (this.labelEl && label && this.labelEl.textContent !== label) this.labelEl.textContent = label;

    const colour = this.root.querySelector('input[data-zb-colour-value][data-zb-colour-display]:checked');
    const size = this.root.querySelector('fieldset.zb-size input:checked');
    const parts = [];
    if (colour?.dataset.zbColourDisplay) parts.push(colour.dataset.zbColourDisplay);
    const sizeText = this.sizeText(size);
    if (sizeText) parts.push(sizeText);
    const price = this.price();
    if (price) parts.push(price);
    const line = parts.join(' · ');
    if (this.lineEl && this.lineEl.textContent !== line) this.lineEl.textContent = line;

    this.fillPhoto(colour);
  }

  /** The checked size chip's number in the active unit: "<UNIT> <number>" (no-break space), or the plain number. */
  sizeText(input) {
    const label = input?.closest('label');
    if (!label) return '';
    const nums = [...label.querySelectorAll('.zb-size__num')];
    if (!nums.length) return '';
    const unit = this.root.dataset.zbUnit;
    const num = nums.find((n) => n.dataset.zbUnit && n.dataset.zbUnit === unit) ?? nums.find(isRendered) ?? nums[0];
    const copy = num.cloneNode(true);
    copy.querySelectorAll('.visually-hidden').forEach((n) => n.remove());
    const value = copy.textContent.trim();
    if (!value) return '';
    return num.dataset.zbUnit ? `${num.dataset.zbUnit}${NBSP}${value}` : value;
  }

  price() {
    let id = this.state.variantId;
    if (!id) id = this.root.querySelector('form[id^="BuyButtons-ProductForm-"] [name="id"]')?.value ?? null;
    return id ? (this.variants[id]?.p ?? '') : '';
  }

  fillPhoto(colour) {
    if (!this.photoBox || !this.photoImg) return;
    const img = colour?.closest('label.zb-tile')?.querySelector('img');
    const src = img ? img.currentSrc || img.src : '';
    if (!src) {
      this.photoBox.hidden = true;
      return;
    }
    this.photoBox.hidden = false;
    if (src !== this.lastSrc) {
      this.lastSrc = src;
      this.photoImg.src = src;
    }
  }

  onTap() {
    const mode = this.bar.dataset.zbMode;
    if (mode === 'buy') {
      const add = this.addButton;
      if (!isRendered(add) || add.disabled) return;
      this.showBusy();
      add.click();
    } else if (mode === 'notify') {
      this.goToNotify();
    }
  }

  /** 500 ms spinner on the bar button after a buy tap (static with reduced motion, see the CSS). */
  showBusy() {
    this.button.setAttribute('aria-busy', 'true');
    clearTimeout(this.busyTimer);
    this.busyTimer = setTimeout(() => this.clearBusy(), BUSY_MS);
  }

  clearBusy() {
    clearTimeout(this.busyTimer);
    this.button.removeAttribute('aria-busy');
  }

  goToNotify() {
    const target = this.notifyTarget;
    const field = this.notifyField;
    if (!target || !isRendered(field)) return;
    // focus inside the tap so the phone keyboard opens; the scroll follows
    field.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'center', behavior: reducedMotion.matches ? 'instant' : 'smooth' });
  }
}

function init() {
  for (const bar of document.querySelectorAll('[data-zb-bar]')) {
    if (started.has(bar)) continue;
    started.add(bar);
    new ZerbayStickyBar(bar);
  }
}

init();
document.addEventListener('shopify:section:load', init);
